import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldAlert } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const EmailSettings = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["email-settings"], queryFn: () => api.get("/admin/email-settings").then((r) => r.data) });
  const [form, setForm] = useState({ resend_from: "", reply_to: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data) setForm({ resend_from: data.is_test_sender ? "" : data.resend_from || "", reply_to: data.reply_to || "" });
  }, [data]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/email-settings", form);
      toast.success("Email sender saved");
      qc.invalidateQueries({ queryKey: ["email-settings"] });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const verified = data && !data.is_test_sender && data.resend_from;
  const managed = data && !verified && data.managed_available;
  return (
    <form onSubmit={save} data-testid="admin-email-settings" className="rounded-2xl bg-[#1B2A41] border border-[#2C3E55] p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-type font-bold text-lg">Sender settings</h3>
          <p className="text-xs text-[#F8FAFC]/50 mt-1">Emails currently go out through the managed ROAN sender; replies land in the reply‑to inbox below. Optional: verify your own domain with Resend and enter an address on it to send from your own name.</p>
        </div>
        <span data-testid="admin-email-sender-status" className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border whitespace-nowrap ${verified || managed ? "border-[#8FA58A] text-[#8FA58A]" : "border-[#E2705A]/60 text-[#E2705A]"}`}>
          {verified || managed ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />} {verified ? `Sending from ${data.resend_from}` : managed ? `Managed sender · replies to ${data.reply_to || "—"}` : "No sender configured"}
        </span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="font-type text-[11px] uppercase tracking-wider text-[#8FA58A]">Verified sender address</Label>
          <Input data-testid="admin-email-from-input" type="email" value={form.resend_from} onChange={(e) => setForm({ ...form, resend_from: e.target.value })} placeholder="jody@yourdomain.com.au" className="h-10 bg-[#121E2B] border-[#2C3E55]" />
        </div>
        <div className="space-y-1.5">
          <Label className="font-type text-[11px] uppercase tracking-wider text-[#8FA58A]">Reply‑to inbox</Label>
          <Input data-testid="admin-email-replyto-input" type="email" value={form.reply_to} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} placeholder="jody@yourdomain.com.au" className="h-10 bg-[#121E2B] border-[#2C3E55]" />
        </div>
      </div>
      <button type="submit" disabled={busy} data-testid="admin-email-settings-save" className="h-10 px-5 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-60 font-type font-bold uppercase tracking-wider text-xs inline-flex items-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save sender
      </button>
    </form>
  );
};
